import { useEffect, type JSX } from 'react';
import { EMPTY, useEngine } from '../../state/engineStore';
import { playHorn, stopHorn } from '../../audio/horn';
import type { AlarmSnapshot } from '../../engine/alarms/alarms';

export const PRI_COLOUR: Record<string, string> = {
  emergency: 'var(--pri-emergency)',
  high: 'var(--pri-high)',
  medium: 'var(--pri-medium)',
  low: 'var(--pri-low)',
};

/** Priority shape — the second, non-colour signal (CLAUDE.md rule 10). */
export function PriorityMark({ priority, size = 13 }: { priority: string; size?: number }): JSX.Element {
  const c = PRI_COLOUR[priority] ?? 'var(--line)';
  const s = size;
  if (priority === 'emergency') {
    return (
      <svg width={s} height={s} viewBox="0 0 12 12" aria-label="emergency">
        <path d="M6 0 L12 6 L6 12 L0 6 Z" fill={c} />
        <text x={6} y={8.4} textAnchor="middle" fontSize={7} fill="#160000" fontWeight={700}>
          1
        </text>
      </svg>
    );
  }
  if (priority === 'high') {
    return (
      <svg width={s} height={s} viewBox="0 0 12 12" aria-label="high">
        <path d="M6 0.5 L11.5 11 L0.5 11 Z" fill={c} />
        <text x={6} y={10} textAnchor="middle" fontSize={6.5} fill="#231400" fontWeight={700}>
          2
        </text>
      </svg>
    );
  }
  if (priority === 'medium') {
    return (
      <svg width={s} height={s} viewBox="0 0 12 12" aria-label="medium">
        <rect x={1} y={1} width={10} height={10} fill={c} />
        <text x={6} y={9} textAnchor="middle" fontSize={7} fill="#221f00" fontWeight={700}>
          3
        </text>
      </svg>
    );
  }
  return (
    <svg width={s} height={s} viewBox="0 0 12 12" aria-label="low">
      <circle cx={6} cy={6} r={5} fill={c} />
      <text x={6} y={8.6} textAnchor="middle" fontSize={7} fill="#001824" fontWeight={700}>
        4
      </text>
    </svg>
  );
}

export function stateLabel(a: AlarmSnapshot): string {
  if (a.shelved) return 'SHELVED';
  if (a.oos) return 'OUT OF SERVICE';
  if (a.suppressed) return 'SUPPRESSED';
  switch (a.state) {
    case 'unackedActive':
      return 'UNACK';
    case 'ackedActive':
      return 'ACK';
    case 'unackedRtn':
      return 'RTN-UNACK';
    default:
      return 'NORMAL';
  }
}

export function AlarmBanner(): JSX.Element | null {
  const alarms = useEngine((s) => s.snap?.alarms ?? (EMPTY as unknown as AlarmSnapshot[]));
  const metrics = useEngine((s) => s.snap?.alarmMetrics);
  const horn = useEngine((s) => s.snap?.hornActive ?? false);
  const muted = useEngine((s) => s.hornMuted);
  const send = useEngine((s) => s.send);
  const setScreen = useEngine((s) => s.setScreen);

  const unacked = alarms.filter((a) => a.state === 'unackedActive' || a.state === 'unackedRtn');
  const top = unacked[0] ?? alarms[0] ?? null;

  useEffect(() => {
    if (horn && !muted && unacked[0]) playHorn(unacked[0].priority);
    else stopHorn();
  }, [horn, muted, unacked[0]?.id, unacked[0]?.priority]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '4px 10px',
        minHeight: 30,
        background: top ? 'var(--bg-deep)' : 'var(--panel)',
        borderBottom: '1px solid var(--line-dim)',
        borderLeft: top ? `4px solid ${PRI_COLOUR[top.priority] ?? 'var(--line)'}` : '4px solid transparent',
      }}
    >
      {top ? (
        <>
          <span className={unacked.length > 0 ? 'flash' : undefined} style={{ display: 'flex', alignItems: 'center' }}>
            <PriorityMark priority={top.priority} size={15} />
          </span>
          <button
            onClick={() => setScreen('alarms')}
            style={{ background: 'transparent', border: 'none', padding: 0, textAlign: 'left', flex: 1, minWidth: 0 }}
          >
            <span className="num muted" style={{ fontSize: 11 }}>
              {top.tag}
            </span>{' '}
            <span style={{ fontWeight: 600 }}>{top.text}</span>{' '}
            <span className="muted" style={{ fontSize: 11 }}>
              {stateLabel(top)} · {top.value.toFixed(2)}
              {top.limit !== null ? ` (limit ${top.limit.toFixed(2)})` : ''}
            </span>
          </button>
          <span className="muted num" style={{ fontSize: 11 }}>
            {unacked.length} unack · {alarms.length} active
          </span>
          <button onClick={() => send({ t: 'silence' })} title="Silence the horn — this is not an acknowledgement">
            Silence
          </button>
          <button onClick={() => send({ t: 'ackAll' })} title="Acknowledge every alarm (Shift+A)">
            Ack all
          </button>
        </>
      ) : (
        <>
          <span style={{ width: 15, height: 15, borderRadius: 2, border: '1px solid var(--line-dim)' }} />
          <span className="muted" style={{ flex: 1 }}>
            No active alarms
          </span>
          <span className="muted num" style={{ fontSize: 11 }}>
            {metrics ? `${metrics.per10min}/10 min · ${metrics.standing} standing` : ''}
          </span>
        </>
      )}
    </div>
  );
}
